import { useId, useMemo, useRef, useState, type FormEvent } from 'react';
import {
  apiErrorText,
  useCreateMtoReceiptMutation,
  useDeleteMtoReceiptMutation,
  useGetMtoReceiptHistoryQuery,
  useUpdateMtoReceiptMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerFooter,
  DrawerHeader,
} from '@shared/ui/Drawer';
import {
  Actions,
  CheckLabel,
  Control,
  FieldError,
  FieldHint,
  FieldLabel,
  FleetChip,
  FooterStatus,
  FormGrid,
  FormRow,
  Notice,
  Spacer,
  typo,
} from '@entities/fleet';
import {
  MATERIAL_KIND_LABEL,
  RECEIPT_FIELD_LABEL,
  formatDate,
  type Receipt,
  type ReceiptInput,
} from '@entities/mto/receipts';
import { DrawerTitle, Eyebrow, HeaderMeta, HeaderRow, Titles } from './drawerParts';
import { HistoryList, Section } from './styles';

type Values = Record<keyof Omit<ReceiptInput, 'vat_free'>, string> & { vat_free: boolean };

const TEXT_FIELDS: readonly {
  name: keyof Omit<ReceiptInput, 'vat_free'>;
  label: string;
  required?: boolean;
  type?: 'date' | 'number';
  wide?: boolean;
  hint?: string;
}[] = [
  {
    name: 'receipt_date',
    label: 'Дата поступления',
    required: true,
    type: 'date',
    hint: 'По ней материал попадает в месяц БДР — даже если документ пришёл позже.',
  },
  {
    name: 'object_name',
    label: 'Объект',
    required: true,
    hint: 'Как в книге: «БОВ (проект)» — материал по проекту, «БОВ» — прочие, «Склад», «ОГМ».',
  },
  { name: 'supplier', label: 'Поставщик', required: true, wide: true },
  { name: 'item_name', label: 'Наименование', required: true, wide: true },
  { name: 'invoice_no', label: 'Счёт-фактура / УПД №' },
  { name: 'payment_invoice_no', label: 'Счёт на оплату' },
  { name: 'quantity', label: 'Количество', type: 'number' },
  { name: 'unit', label: 'Ед. изм.' },
  { name: 'price', label: 'Цена без НДС', type: 'number' },
  {
    name: 'amount',
    label: 'Сумма без НДС',
    type: 'number',
    hint: 'Пусто — посчитается: количество × цена.',
  },
  { name: 'vat_amount', label: 'НДС', type: 'number' },
  { name: 'amount_with_vat', label: 'Сумма с НДС', type: 'number', hint: 'Пусто — сумма + НДС.' },
  { name: 'signed_by', label: 'Кто принял' },
  { name: 'original_sent_on', label: 'Оригинал передан в бухгалтерию', type: 'date' },
  { name: 'act_no', label: 'Акт' },
  { name: 'ttn_no', label: 'ТТН' },
  { name: 'tn_no', label: 'ТН' },
  { name: 'item_size', label: 'Размер / объём' },
  { name: 'transferred_to', label: 'Передано субподрядчику' },
  { name: 'transfer_act', label: '№ акта передачи' },
  { name: 'uptk_info', label: 'Доп. информация (УПТК)' },
  { name: 'uptk_upd_no', label: '№ УПД (УПТК)' },
  { name: 'note', label: 'Примечание', wide: true },
];

function emptyValues(): Values {
  const text = Object.fromEntries(TEXT_FIELDS.map((f) => [f.name, ''])) as Record<
    keyof Omit<ReceiptInput, 'vat_free'>,
    string
  >;
  return { ...text, vat_free: false, receipt_date: new Date().toISOString().slice(0, 10) };
}

/** Число строки — как ввёл бы человек: «2418.03» → «2418,03». */
function numText(v: string | null): string {
  return v === null ? '' : v.replace('.', ',');
}

function valuesFrom(r: Receipt): Values {
  return {
    receipt_date: r.receipt_date,
    invoice_no: r.invoice_no ?? '',
    act_no: r.act_no ?? '',
    ttn_no: r.ttn_no ?? '',
    tn_no: r.tn_no ?? '',
    payment_invoice_no: r.payment_invoice_no ?? '',
    supplier: r.supplier,
    item_name: r.item_name,
    item_size: r.item_size ?? '',
    quantity: numText(r.quantity),
    unit: r.unit ?? '',
    price: numText(r.price),
    amount: numText(r.amount),
    vat_amount: numText(r.vat_amount),
    vat_free: r.vat_free,
    amount_with_vat: numText(r.amount_with_vat),
    object_name: r.object_name ?? '',
    signed_by: r.signed_by ?? '',
    note: r.note ?? '',
    original_sent_on: r.original_sent_on ?? '',
    uptk_info: r.uptk_info ?? '',
    uptk_upd_no: r.uptk_upd_no ?? '',
    transferred_to: r.transferred_to ?? '',
    transfer_act: r.transfer_act ?? '',
  };
}

function bodyFrom(v: Values): ReceiptInput {
  const t = (s: string) => (s.trim() === '' ? null : s.trim());
  return {
    receipt_date: v.receipt_date,
    invoice_no: t(v.invoice_no),
    act_no: t(v.act_no),
    ttn_no: t(v.ttn_no),
    tn_no: t(v.tn_no),
    payment_invoice_no: t(v.payment_invoice_no),
    supplier: v.supplier.trim(),
    item_name: v.item_name.trim(),
    item_size: t(v.item_size),
    quantity: t(v.quantity),
    unit: t(v.unit),
    price: t(v.price),
    amount: t(v.amount),
    vat_amount: v.vat_free ? null : t(v.vat_amount),
    vat_free: v.vat_free,
    amount_with_vat: t(v.amount_with_vat),
    object_name: t(v.object_name),
    signed_by: t(v.signed_by),
    note: t(v.note),
    original_sent_on: t(v.original_sent_on),
    uptk_info: t(v.uptk_info),
    uptk_upd_no: t(v.uptk_upd_no),
    transferred_to: t(v.transferred_to),
    transfer_act: t(v.transfer_act),
  };
}

const NUMBER_RE = /^-?[\d\s ]*[.,]?\d*$/;

function issuesOf(v: Values): Partial<Record<string, string>> {
  const out: Partial<Record<string, string>> = {};
  for (const f of TEXT_FIELDS) {
    const val = v[f.name].trim();
    if (f.required && val === '') out[f.name] = 'Заполните поле';
    else if (f.type === 'number' && val !== '' && (!NUMBER_RE.test(val) || !/\d/.test(val)))
      out[f.name] = 'Только число, например 2418,03';
  }
  if (!out.amount && v.amount.trim() === '' && (v.quantity.trim() === '' || v.price.trim() === ''))
    out.amount = 'Укажите сумму или количество и цену';
  return out;
}

/**
 * Строка прихода в боковой панели: новая или существующая. Ошибка — под
 * своим полем; ниже формы — журнал правок «кто, когда, было → стало».
 */
export function ReceiptDrawer({
  receipt,
  canWrite,
  objectNames,
  onClose,
  onSaved,
}: {
  receipt: Receipt | null;
  canWrite: boolean;
  /** Написания объектов из справочника — подсказки при вводе. */
  objectNames: readonly string[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const uid = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const initial = useMemo(() => (receipt ? valuesFrom(receipt) : emptyValues()), [receipt]);
  const [values, setValues] = useState<Values>(initial);
  const [showIssues, setShowIssues] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [create, createState] = useCreateMtoReceiptMutation();
  const [update, updateState] = useUpdateMtoReceiptMutation();
  const [remove, removeState] = useDeleteMtoReceiptMutation();
  const { data: history } = useGetMtoReceiptHistoryQuery(receipt?.id ?? '', { skip: !receipt });
  const busy = createState.isLoading || updateState.isLoading || removeState.isLoading;
  const issues = useMemo(() => issuesOf(values), [values]);
  const dirty = (Object.keys(values) as (keyof Values)[]).filter(
    (k) => values[k] !== initial[k],
  ).length;
  const title = receipt ? receipt.item_name : 'Новая строка прихода';

  function close() {
    if (dirty > 0 && !busy && !window.confirm('Закрыть без сохранения? Изменения пропадут.'))
      return;
    onClose();
  }

  function set<K extends keyof Values>(name: K, value: Values[K]) {
    setValues((v) => ({ ...v, [name]: value }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (Object.keys(issues).length > 0) {
      setShowIssues(true);
      const first =
        TEXT_FIELDS.find((f) => issues[f.name]) ?? (issues.amount ? { name: 'amount' } : undefined);
      if (first) document.getElementById(`${uid}-${first.name}`)?.focus();
      return;
    }
    try {
      const body = bodyFrom(values);
      if (receipt) {
        await update({ id: receipt.id, body }).unwrap();
        onSaved(`Строка прихода «${body.item_name}» сохранена.`);
      } else {
        await create(body).unwrap();
        onSaved(`Строка прихода «${body.item_name}» добавлена.`);
      }
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось сохранить строку прихода.'));
    }
  }

  async function onDelete() {
    if (!receipt) return;
    if (
      !window.confirm(
        'Удалить строку прихода? Она уйдёт из журнала и из БДР, в журнале правок останется.',
      )
    )
      return;
    setError(null);
    try {
      await remove(receipt.id).unwrap();
      onSaved(`Строка прихода «${receipt.item_name}» удалена.`);
    } catch (err) {
      setError(apiErrorText(err, 'Не удалось удалить строку прихода.'));
    }
  }

  return (
    <Drawer open onClose={close} labelledBy={`${uid}-title`} initialFocusRef={titleRef} size="lg">
      <DrawerHeader>
        <HeaderRow>
          <Titles>
            <Eyebrow>
              Приход ТМЦ
              {receipt && ` · ${formatDate(receipt.receipt_date)}`}
            </Eyebrow>
            <DrawerTitle id={`${uid}-title`} ref={titleRef} tabIndex={-1}>
              {title}
            </DrawerTitle>
            {receipt && (
              <HeaderMeta>
                {receipt.object_target ? (
                  <FleetChip $tone="success">
                    → {receipt.object_target}
                    {receipt.material_kind &&
                      ` · ${MATERIAL_KIND_LABEL[receipt.material_kind].toLowerCase()}`}
                  </FleetChip>
                ) : (
                  <FleetChip $tone="warning">объект не сопоставлен</FleetChip>
                )}
                <FleetChip $tone="muted">
                  {receipt.source === 'import'
                    ? `из книги, лист ${receipt.source_sheet ?? '—'}, строка ${receipt.source_row ?? '—'}`
                    : 'внесена в ОСНОВЕ'}
                </FleetChip>
              </HeaderMeta>
            )}
          </Titles>
          <DrawerCloseButton onClick={close} />
        </HeaderRow>
      </DrawerHeader>
      <DrawerBody>
        {error && (
          <Notice $tone="danger" role="alert">
            <span className="text">{error}</span>
          </Notice>
        )}
        {receipt && receipt.issues.length > 0 && (
          <Notice $tone="warning" role="note">
            <span className="text">{typo(`При загрузке: ${receipt.issues.join('; ')}.`)}</span>
          </Notice>
        )}
        {!canWrite && (
          <Notice $tone="info" role="note">
            <span className="text">{typo('Только просмотр: приход ведёт отдел МТО.')}</span>
          </Notice>
        )}
        <form id={`${uid}-form`} onSubmit={(e) => void submit(e)} noValidate>
          <datalist id={`${uid}-objects`}>
            {objectNames.map((n) => (
              <option key={n} value={n} />
            ))}
          </datalist>
          <FormGrid>
            {TEXT_FIELDS.map((f) => {
              const id = `${uid}-${f.name}`;
              const issue = showIssues ? issues[f.name] : undefined;
              const describedBy =
                [f.hint ? `${id}-hint` : null, issue ? `${id}-error` : null]
                  .filter(Boolean)
                  .join(' ') || undefined;
              const disabled = f.name === 'vat_amount' && values.vat_free;
              return (
                <FormRow key={f.name} $wide={f.wide}>
                  <FieldLabel htmlFor={id}>
                    <span>
                      {f.label}
                      {f.required && (
                        <span className="req" aria-hidden="true">
                          {' '}
                          *
                        </span>
                      )}
                    </span>
                  </FieldLabel>
                  <Control
                    id={id}
                    type={f.type === 'date' ? 'date' : 'text'}
                    inputMode={f.type === 'number' ? 'decimal' : undefined}
                    list={f.name === 'object_name' ? `${uid}-objects` : undefined}
                    autoComplete="off"
                    value={values[f.name]}
                    readOnly={!canWrite}
                    disabled={busy || disabled}
                    aria-required={f.required ? true : undefined}
                    aria-invalid={issue ? true : undefined}
                    aria-describedby={describedBy}
                    onChange={(e) => set(f.name, e.target.value)}
                  />
                  {f.hint && <FieldHint id={`${id}-hint`}>{typo(f.hint)}</FieldHint>}
                  {issue && <FieldError id={`${id}-error`}>{issue}</FieldError>}
                  {f.name === 'vat_amount' && (
                    <CheckLabel>
                      <input
                        type="checkbox"
                        checked={values.vat_free}
                        disabled={!canWrite || busy}
                        onChange={(e) => set('vat_free', e.target.checked)}
                      />
                      Без НДС
                    </CheckLabel>
                  )}
                </FormRow>
              );
            })}
          </FormGrid>
        </form>

        {receipt && (
          <Section aria-labelledby={`${uid}-history`}>
            <h3 id={`${uid}-history`}>Журнал правок</h3>
            {!history || history.items.length === 0 ? (
              <p>
                {typo(
                  receipt.source === 'import'
                    ? 'Строка загружена из книги и в ОСНОВЕ не менялась.'
                    : 'Правок нет.',
                )}
              </p>
            ) : (
              <HistoryList>
                {history.items.map((h) => (
                  <li key={h.id}>
                    <div className="who">
                      {new Date(h.at).toLocaleString('ru-RU')} · {h.actor_name ?? 'система'} ·{' '}
                      {h.action === 'create'
                        ? 'внесена'
                        : h.action === 'delete'
                          ? 'удалена'
                          : 'изменена'}
                    </div>
                    {h.action === 'update' &&
                      Object.entries(h.diff).map(([k, d]) => (
                        <div key={k} className="change">
                          {RECEIPT_FIELD_LABEL[k] ?? k}: <del>{d.from ?? '—'}</del> → {d.to ?? '—'}
                        </div>
                      ))}
                  </li>
                ))}
              </HistoryList>
            )}
          </Section>
        )}
      </DrawerBody>
      <DrawerFooter>
        {canWrite && receipt && (
          <Button type="button" $variant="ghost" disabled={busy} onClick={() => void onDelete()}>
            Удалить
          </Button>
        )}
        <FooterStatus role="status">
          {busy ? 'Сохраняю…' : dirty > 0 ? `Изменено: ${dirty}` : ''}
        </FooterStatus>
        <Spacer />
        <Actions>
          <Button type="button" $variant="ghost" disabled={busy} onClick={close}>
            {canWrite ? 'Отмена' : 'Закрыть'}
          </Button>
          {canWrite && (
            <Button
              type="submit"
              form={`${uid}-form`}
              $variant="primary"
              disabled={busy || (receipt !== null && dirty === 0)}
            >
              {receipt ? 'Сохранить' : 'Добавить'}
            </Button>
          )}
        </Actions>
      </DrawerFooter>
    </Drawer>
  );
}
